import { basePath } from '@/api/constants'
import { getToken } from '@/api/http-service/PrepareAPIService'
import {
  EventSourceMessage,
  fetchEventSource,
} from '@microsoft/fetch-event-source'

/**
 * Send a conversational message that can trigger prepare tasks
 */
export const sendConversationalMessage = async (
  threadId: string,
  message: string,
  sheetId?: string
): Promise<any> => {
  const url = `${basePath}/agent/chat/${threadId}`
  const token = getToken()

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message,
        sheetId: sheetId || undefined,
        metadata: {
          threadId,
        },
      }),
    })

    if (!response.ok) {
      throw new Error(
        `Failed to send conversational message: ${response.status} ${response.statusText}`
      )
    }

    return response.json()
  } catch (error) {
    console.error('Send conversational message error', error)
    throw error
  }
}

/**
 * Start a prepare task associated with a thread
 */
export const startPrepareTask = async (
  threadId: string,
  instructions?: string,
  sheetId?: string,
  columnConfig?: any,
  artifactId?: string,
  projectId?: string
): Promise<any> => {
  const url = createPrepareTaskUrl(threadId)
  const token = getToken()
  if (!token) {
    throw new Error(
      'Authentication token not available. Please ensure you are authenticated.'
    )
  }
  let formattedColumnConfig: Array<{ key: string; label: string }> | undefined =
    undefined
  if (columnConfig) {
    formattedColumnConfig = columnConfig.map((column) => ({
      key: column.value,
      label: column.label,
    }))
  }
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        instructions,
        sheetId: sheetId || undefined, // Convert empty string to undefined
        columnConfig: formattedColumnConfig,
        artifactId,
        projectId,
      }),
    })

    if (!response.ok) {
      throw new Error(
        `Failed to start prepare task: ${response.status} ${response.statusText}`
      )
    }

    return response.json()
  } catch (error) {
    console.error('Start prepare task error', error)
    throw error
  }
}

/**
 * Parse an SSE message event into the correct format
 */
const parseSSEMessage = (event) => {
  // Check if the event has an event type
  const eventType = event.event

  // Get the data (which may be a string or already parsed JSON)
  let data = event.data

  // If data is a string, try to parse it as JSON
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch (e) {
      return {
        event: eventType,
        raw: data,
      }
    }
  }

  // Create our standardized result
  const result = {
    event: eventType,
    data: data,
  }

  return result
}

type EventCallbacks = {
  onProgress?: (message: any) => void
  onError?: (error: Error) => void
  onComplete?: () => void
}

/**
 * Subscribe to resource events via SSE with resilient connection
 * This is a generic function that replaces the separate thread and project subscription functions
 */
export const subscribeToResourceEvents = async (
  resourceType: string,
  resourceId: string,
  callbacks: EventCallbacks,
  signal?: AbortSignal
): Promise<void> => {
  const { onProgress, onError, onComplete } = callbacks
  const url = createResourceStreamUrl(resourceType, resourceId)

  let lastHeartbeat = Date.now()
  let heartbeatInterval: NodeJS.Timeout | null = null
  let eventCount = 0

  try {
    // Start a heartbeat monitor that will detect if connection is broken
    heartbeatInterval = setInterval(() => {
      const now = Date.now()
      // If no heartbeat for 30 seconds, we consider the connection dead
      if (now - lastHeartbeat > 30000) {
        console.warn(
          `No ${resourceType} heartbeat received for 30 seconds, connection may be dead`
        )
      }
    }, 10000)

    const token = getToken()
    if (!token) {
      throw new Error(
        'Authentication token not available. Please ensure you are authenticated.'
      )
    }

    await fetchEventSource(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
        Connection: 'keep-alive',
      },
      credentials: 'include', // Include cookies for CORS
      signal,
      // Only fetch from network, never from cache
      cache: 'no-store',

      // Handle opened connection
      async onopen(response: Response) {
        // Update heartbeat
        lastHeartbeat = Date.now()

        if (
          response.ok &&
          response.headers.get('content-type')?.includes('text/event-stream')
        ) {
          return
        }

        if (response.status === 404) {
          // Resource not found - don't retry
          console.error(`${resourceType} not found, ending connection attempts`)
          throw new Error(`${resourceType} not found`)
        }

        // For other errors, throw to trigger retry logic
        throw new Error(
          `Failed to connect to ${resourceType} stream: ${response.status} ${response.statusText}`
        )
      },

      // Parse messages
      onmessage(event: EventSourceMessage) {
        // Update heartbeat timestamp on any message
        lastHeartbeat = Date.now()
        eventCount++

        // Skip if no data
        if (!event.data) return

        // Handle different event types
        if (event.event === 'heartbeat') {
          return
        }

        // Handle error events
        if (event.event === 'error') {
          console.error('Server sent error event:', event.data)
          onError?.(new Error(event.data))
          return
        }

        // Process events
        try {
          // Parse the message into the correct format
          const parsedMessage = parseSSEMessage(event)

          if (parsedMessage && onProgress) {
            onProgress(parsedMessage)
          }
        } catch (error) {
          console.error(
            `Error processing ${resourceType} SSE message:`,
            error,
            event
          )
          // Don't fail the connection for parse errors
        }
      },

      // Handle errors with retry logic
      onerror(error: Error): void | number {
        console.error(`${resourceType} EventSource error:`, error)

        // Only call onError if it's a fatal error
        if (error.message.includes(`${resourceType} not found`)) {
          onError?.(error)
          return
        }

        // For network errors, just log but let retry happen
        console.warn(`${resourceType} connection error, will retry...`)
        return 1 // retry with default delay
      },

      // Handle normal close
      onclose() {
        if (heartbeatInterval) {
          clearInterval(heartbeatInterval)
        }
        onComplete?.()
      },
    })
  } catch (error) {
    console.error(
      `${resourceType} subscription error for ${resourceId}:`,
      error
    )
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval)
    }
    onError?.(error as Error)
  }
}

/**
 * Create a URL for the prepare task endpoint
 */
export const createPrepareTaskUrl = (threadId: string): string => {
  return `${basePath}/agent/prepare/${threadId}`
}

/**
 * Create a URL for resource stream endpoint
 */
export const createResourceStreamUrl = (
  resourceType: string,
  resourceId: string
): string => {
  const createdUrl = `${basePath}/event-stream/${resourceType}.${resourceId}`

  // Use standard dot notation for topic format
  return createdUrl
}

/**
 * For backward compatibility - will be removed
 */
export const createThreadStreamUrl = (threadId: string): string => {
  return createResourceStreamUrl('thread', threadId)
}

/**
 * For backward compatibility - will be removed
 */
export const createProjectStreamUrl = (projectId: string): string => {
  return createResourceStreamUrl('project', projectId)
}

// Type definition for prepare chat request parameters
export interface PrepareChatRequestParams {
  threadId?: string
  sourceSheetId?: string
  userInstructions?: string
  instructions?: string
  messageHistory?: string
  onProgress?: (message: any) => void
  onError?: (error: Error) => void
  onComplete?: () => void
  signal?: AbortSignal
}

// // Legacy method maintained for backward compatibility
// export const prepareChat = async (
//   requestParameters: PrepareChatRequestParams
// ): Promise<void> => {
//   const {
//     threadId,
//     sourceSheetId,
//     userInstructions,
//     instructions,
//     messageHistory,
//     onProgress,
//     onError,
//     onComplete,
//     signal,
//   } = requestParameters

//   if (threadId) {
//     try {
//       // First start the prepare task
//       await startPrepareTask(threadId, instructions || userInstructions, sourceSheetId, columnConfig, artifactId, projectId)

//       // Then subscribe to the thread events
//       await subscribeToResourceEvents(
//         'thread',
//         threadId,
//         { onProgress, onError, onComplete },
//         signal
//       )
//     } catch (error) {
//       console.error('Thread-based prepare error', error)
//       onError?.(error as Error)
//       throw error
//     }
//   } else {
//     throw new Error('threadId must be provided')
//   }
// }
